if (!customElements.get('custom-header')) {
  class CustomHeader extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.getAttribute('data-section-id');
      this.dropdownToggles = this.querySelectorAll('[js-dropdown-toggle]');
      this.dropdownPanels = this.querySelectorAll('[js-dropdown-panel]');

      this.menuTrigger = this.querySelector('.js-offcanvas-trigger.new-menu-trigger');

      this.boundMouseEnterHandler = this.toggleMouseEnterHandler.bind(this);
      this.boundMouseLeaveHandler = this.toggleMouseLeaveHandler.bind(this);
      this.boundToggleMouseEnterHandler = this.toggleMouseEnterHandler.bind(this);
      this.boundPanelMouseEnterHandler = this.panelMouseEnterHandler.bind(this);
      this.boundPanelMouseLeaveHandler = this.panelMouseLeaveHandler.bind(this);
      this.boundToggleMobileMenu = this.toggleMobileMenu.bind(this);
    }

    isTouchEnabled() {
      return 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
    }

    connectedCallback() {
      this.dropdownToggles.forEach((toggle) => {
        toggle.addEventListener('click', this.boundToggleClickHandler);
      });

      /* bound on all widths: the drawer is also used on desktop when the
         nav links don't fit on one row (html.nav-collapsed).
         Explicit .Offcanvas__new: on collection pages a bare .Offcanvas--left
         also matches the facets drawer. */
      this.offCanvasElement = document.querySelector('.Offcanvas--left.Offcanvas__new');

      this.menuTrigger?.addEventListener('click', this.boundToggleMobileMenu);

      if (!this.isTouchEnabled()) {
        this.dropdownToggles.forEach((toggle) => {
          toggle.addEventListener('mouseenter', this.boundToggleMouseEnterHandler);
        });
        this.dropdownPanels.forEach((panel) => {
          panel.addEventListener('mouseenter', this.boundPanelMouseEnterHandler);
          panel.addEventListener('mouseleave', this.boundPanelMouseLeaveHandler);
        });
      }

      this.setHeaderHeight();
      /* the nav-collapsed watcher lives inline in sections/header.liquid so it
         runs at parse time — waiting for this deferred script caused a visible
         desktop-then-mobile flash of the nav on load */
    }

    disconnectedCallback() {}

    setHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
    }

    removeFromFocusOrder = (elements) => {
      elements.forEach((element) => {
        element.setAttribute('tabindex', -1);
      });
    };

    restoreFocusOrder = (elements) => {
      elements.forEach((element) => {
        element.removeAttribute('tabindex');
      });
    };

    closeDropdown(toggleElement) {
      const dropdownId = toggleElement.getAttribute('aria-controls');
      const dropdownPanel = document.getElementById(dropdownId);
      const childLinks = dropdownPanel?.querySelectorAll('a');
      const childButtons = dropdownPanel?.querySelectorAll('button');

      toggleElement.setAttribute('aria-expanded', 'false');
      dropdownPanel?.setAttribute('aria-hidden', 'true');
      dropdownPanel?.classList.remove('visible');
      this.removeFromFocusOrder(childLinks);
      this.removeFromFocusOrder(childButtons);
    }

    openDropdown(toggleElement) {
      const dropdownId = toggleElement.getAttribute('aria-controls');
      const dropdownPanel = document.getElementById(dropdownId);
      const childLinks = dropdownPanel?.querySelectorAll('a');
      const childButtons = dropdownPanel?.querySelectorAll('button');

      this.dropdownToggles.forEach((element) => {
        this.closeDropdown(element);
      });

      toggleElement.setAttribute('aria-expanded', 'true');
      dropdownPanel?.setAttribute('aria-hidden', 'false');
      dropdownPanel?.classList.add('visible');
      this.restoreFocusOrder(childLinks);
      this.restoreFocusOrder(childButtons);
    }

    toggleDropdown(toggleElement) {
      const expanded = JSON.parse(toggleElement.getAttribute('aria-expanded'));

      if (expanded) {
        this.closeDropdown(toggleElement);
      } else {
        this.openDropdown(toggleElement);
      }
    }

    toggleClickHandler(event) {
      this.toggleDropdown(event.currentTarget);
    }

    toggleMouseEnterHandler(event) {
      this.openDropdown(event.currentTarget);
      event.currentTarget.addEventListener('mouseleave', this.boundMouseLeaveHandler);
      event.currentTarget.removeEventListener('mouseenter', this.boundMouseEnterHandler);
    }

    toggleMouseLeaveHandler(event) {
      const fromElement = event.fromElement || event.originalTarget;
      const toElement = event.toElement || event.relatedTarget;
      const sharedId = fromElement.getAttribute('aria-controls');

      if (toElement && toElement.getAttribute('id') !== sharedId) {
        const self = this;
        this.currentTimeout = setTimeout(function () {
          self.closeDropdown(fromElement);
        }, 250);
        fromElement.removeEventListener('mouseleave', this.boundMouseLeaveHandler);
        fromElement.addEventListener('mouseenter', this.boundMouseEnterHandler);
      }
    }

    panelMouseEnterHandler(event) {
      clearTimeout(this.currentTimeout);
    }

    panelMouseLeaveHandler(event) {
      const fromElement = event.fromElement || event.originalTarget;
      const toElement = event.toElement || event.relatedTarget;
      const sharedId = fromElement.getAttribute('id');
      const toggle = document.querySelector(`[aria-controls="${sharedId}"`);

      if (toElement && toElement.getAttribute('aria-controls') !== sharedId) {
        const self = this;
        this.currentTimeout = setTimeout(function () {
          self.closeDropdown(toggle);
        }, 250);
        fromElement.removeEventListener('mouseleave', this.boundMouseLeaveHandler);
        toggle?.addEventListener('mouseenter', this.boundMouseEnterHandler);
      }
    }

    toggleMobileMenu(event) {
      /* single drawer since the nav A/B test was retired */
      this.offCanvasElement = document.querySelector('.Offcanvas--left.Offcanvas__new');
      if (!this.offCanvasElement) return;
      this.offCanvasElement.classList.toggle('is-expanded');
      if(this.offCanvasElement.classList.contains('is-expanded')) {
        document.body.classList.add('has-open-offcanvas');
        event.currentTarget.classList.add('menu-opened');
        /* measured after the body scroll-lock applies, so this is the header's
           natural bottom edge; consumed by the html.nav-collapsed drawer CSS */
        document.documentElement.style.setProperty(
          '--collapsed-drawer-top',
          `${Math.max(0, Math.round(this.getBoundingClientRect().bottom))}px`
        );
      } else {
        document.body.classList.remove('has-open-offcanvas');
        event.currentTarget.classList.remove('menu-opened');
        document.querySelector('.mobile-submenu__container.opened')?.classList.remove('opened');
      }
    }
  }
  customElements.define('custom-header', CustomHeader);
}
